import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Product, ProductFilters, Category } from '@/types';
import { productsApi, categoriesApi } from '@/services/api';
import toast from 'react-hot-toast';

interface ProductsState {
  products: Product[];
  featuredProducts: Product[];
  categories: Category[];
  currentProduct: Product | null;
  isLoading: boolean;
  isFeaturedLoading: boolean;
  isCategoriesLoading: boolean;
  error: string | null;
  filters: ProductFilters;
  pagination: {
    page: number;
    total: number;
    pages: number;
  };
}

const initialState: ProductsState = {
  products: [],
  featuredProducts: [],
  categories: [],
  currentProduct: null,
  isLoading: false,
  isFeaturedLoading: false,
  isCategoriesLoading: false,
  error: null,
  filters: {
    page: 1,
    limit: 20,
  },
  pagination: {
    page: 1,
    total: 0,
    pages: 0,
  },
};

// Async thunks
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (filters: ProductFilters = {}, { rejectWithValue }) => {
    try {
      // Extract pagination params
      const { skip = 0, limit = 20, page, ...otherFilters } = filters;
      const actualSkip = skip || (page ? (page - 1) * (limit || 20) : 0);
      
      // Call API with pagination params
      const response = await productsApi.getAll({
        ...otherFilters,
        skip: actualSkip,
        limit: limit || 20,
      });
      
      // Backend now returns pagination metadata
      if (response.data && typeof response.data === 'object' && 'items' in response.data) {
        return {
          items: response.data.items || [],
          total: response.data.total || 0,
          page: response.data.page || 1,
          pages: response.data.pages || 1,
        };
      }
      
      // Fallback for old format (array response)
      return {
        items: Array.isArray(response.data) ? response.data : [],
        total: Array.isArray(response.data) ? response.data.length : 0,
        page: page || Math.floor(actualSkip / (limit || 20)) + 1,
        pages: Math.ceil((Array.isArray(response.data) ? response.data.length : 0) / (limit || 20)),
      };
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch products');
    }
  }
);

export const searchProducts = createAsyncThunk(
  'products/searchProducts',
  async ({ query, filters }: { query: string; filters?: ProductFilters }, { rejectWithValue }) => {
    try {
      const response = await productsApi.search(query, filters);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to search products');
    }
  }
);

export const fetchFeaturedProducts = createAsyncThunk(
  'products/fetchFeaturedProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await productsApi.getFeatured();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch featured products');
    }
  }
);

export const fetchMyProducts = createAsyncThunk(
  'products/fetchMyProducts',
  async (params: { skip?: number; limit?: number; include_private?: boolean } = {}, { rejectWithValue }) => {
    try {
      const { skip = 0, limit = 100, include_private = true } = params;
      const response = await productsApi.getMyProducts({ skip, limit, include_private });
      // The API returns an array directly, not paginated
      return {
        items: Array.isArray(response.data) ? response.data : [],
        total: Array.isArray(response.data) ? response.data.length : 0,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch your products');
    }
  }
);

export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await productsApi.getById(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || error.message || 'Failed to fetch product');
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'products/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await categoriesApi.getAll();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch categories');
    }
  }
);

export const createProduct = createAsyncThunk(
  'products/createProduct',
  async (productData: any, { rejectWithValue }) => {
    try {
      const response = await productsApi.create(productData);
      toast.success('Product created successfully');
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to create product';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const createProductWithImages = createAsyncThunk(
  'products/createProductWithImages',
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await productsApi.createWithImages(formData);
      toast.success('Product created successfully');
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to create product';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ id, productData }: { id: number; productData: any }, { rejectWithValue }) => {
    try {
      const response = await productsApi.update(id, productData);
      toast.success('Product updated successfully');
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to update product';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async (id: number, { rejectWithValue }) => {
    try {
      await productsApi.delete(id);
      toast.success('Product deleted successfully');
      return id;
    } catch (error: any) {
      const message = error.detail || 'Failed to delete product';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const toggleProductVisibility = createAsyncThunk(
  'products/toggleProductVisibility',
  async ({ id, isPublic }: { id: number; isPublic: boolean }, { rejectWithValue }) => {
    try {
      const response = await productsApi.updateVisibility(id, isPublic);
      toast.success(`Product ${isPublic ? 'published' : 'unpublished'} successfully`);
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to update product visibility';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<ProductFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 20,
      };
    },
    setCurrentProduct: (state, action: PayloadAction<Product | null>) => {
      state.currentProduct = action.payload;
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Products
      .addCase(fetchProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.items) {
          state.products = action.payload.items;
          state.pagination = {
            page: action.payload.page || 1,
            total: action.payload.total || 0,
            pages: action.payload.pages || 0,
          };
        } else {
          state.products = action.payload;
        }
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch My Products
      .addCase(fetchMyProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMyProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products = action.payload.items || [];
        state.pagination = {
          page: 1,
          total: action.payload.total || 0,
          pages: Math.ceil((action.payload.total || 0) / 20),
        };
        state.error = null;
      })
      .addCase(fetchMyProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Search Products
      .addCase(searchProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.items) {
          state.products = action.payload.items;
          state.pagination = {
            page: action.payload.page || 1,
            total: action.payload.total || 0,
            pages: action.payload.pages || 0,
          };
        } else {
          state.products = action.payload;
        }
        state.error = null;
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch Featured Products
      .addCase(fetchFeaturedProducts.pending, (state) => {
        state.isFeaturedLoading = true;
        state.error = null;
      })
      .addCase(fetchFeaturedProducts.fulfilled, (state, action) => {
        state.isFeaturedLoading = false;
        state.featuredProducts = action.payload;
        state.error = null;
      })
      .addCase(fetchFeaturedProducts.rejected, (state, action) => {
        state.isFeaturedLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch Product by ID
      .addCase(fetchProductById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProduct = action.payload;
        state.error = null;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch Categories
      .addCase(fetchCategories.pending, (state) => {
        state.isCategoriesLoading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.isCategoriesLoading = false;
        state.categories = action.payload;
        state.error = null;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.isCategoriesLoading = false;
        state.error = action.payload as string;
      })
      
      // Create Product
      .addCase(createProduct.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products.unshift(action.payload);
        state.error = null;
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update Product
      .addCase(updateProduct.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.products.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.products[index] = action.payload;
        }
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct = action.payload;
        }
        state.error = null;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Delete Product
      .addCase(deleteProduct.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products = state.products.filter(p => p.id !== action.payload);
        if (state.currentProduct?.id === action.payload) {
          state.currentProduct = null;
        }
        state.error = null;
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setFilters, clearFilters, setCurrentProduct, clearCurrentProduct } = productsSlice.actions;
export { productsSlice };
export default productsSlice.reducer;
